import {useSelector} from "react-redux";
import {
  selectCurrentPlayer,
  selectGameResults,
  selectIsGameStarted,
  selectPhase,
  selectPhaseCompletedPlayers,
  selectPlayersConfirmedSetupPhase,
  selectSharedGameState
} from "../../redux/reducers/sharedStateReducer/selectors";
import React from "react";
import {getPlayerHexColor} from "../../utils/players/getPlayerHexColor";
import './PlayerInfo.css'
import {resolveFactionSkinIconPath} from "../../utils/factions/resolveFactionSkinIconPath";
import RankSkinFrame from '../RankSkinFrame/RankSkinFrame';
import VisibleBotMark from '../VisibleBotMark/VisibleBotMark';
import {PHASES} from "../../constants/phases";
import {LoadedImage} from "../EmblaCarousel/FactionsCarousel";
import {getPolicyImagePath} from "../../utils/policy/getPolicyImagePath";
import {getPlayerNextPolicy} from "../../utils/policy/getPlayerNextPolicy";
import {REGISTRY_SINGLETON} from "../../scenes/Registry";
import {checkIfBotMarkShown} from "../../utils/bots/checkIfBotMarkShown";
import {selectIsParallelSupplyPhase} from "../../utils/gameState/supply/parallelSupplySelectors";
import {getPlayerColorStyle} from "../../utils/gameUi/getPlayerColorStyle";
import {joinClassNames} from "../gameUi/joinClassNames";
import {checkIfAnyGamePreparationPhase} from "../../utils/checkIfAnyGamePreparationPhase";
import {getRankStripe, getTurnStatus, PLAYER_TURN_STATUSES} from "../../utils/players/getPlayerTurnStatus";
import {StatWell} from "../gameUi/StatWell";
import {selectIsFieldCutscenePlaying} from "../../redux/reducers/privateStateReducer/selectors";

export function PlayerInfo(props) {
  const {
    player,
    // Своя карточка — с лентой «вы» на портрете: так себя проще найти в списке
    // (решает PlayersList)
    isOwn = false,
    className,
    onClick,
    onPolicyClick,
    // Подпись под именем — только для админа-зрителя: неявный бот в списке
    // иначе неотличим от человека, а по нему нужно нажимать для перехвата
    controlLabel,
  } = props;
  const {
    id,
    name,
    factionId,
    selectedSkin,
    color,
    coins,
    coinsIncome,
    winPoints,
    winPointsIncome,
  } = player;

  const gameResults = useSelector(selectGameResults);
  const isGameStarted = useSelector(selectIsGameStarted);
  const sharedGameState = useSelector(selectSharedGameState);
  const phase = useSelector(selectPhase);
  const currentPlayer = useSelector(selectCurrentPlayer);
  const phaseCompletedPlayers = useSelector(selectPhaseCompletedPlayers);
  const playersConfirmedSetupPhase = useSelector(selectPlayersConfirmedSetupPhase);
  // Только общее состояние — список так же выглядит и в записи матча
  const isParallelSupplyPhase = useSelector(selectIsParallelSupplyPhase);
  // Заставка поля (облет захваченных городов, рост дохода руин): следующая
  // фаза в состоянии уже началась, но не ходит никто
  const isFieldCutscenePlaying = useSelector(selectIsFieldCutscenePlaying);

  const playerColor = getPlayerHexColor(color);

  const playCardSound = () => {
    REGISTRY_SINGLETON.getSoundManager().playCardOpen();
  }

  // После матча и во время заставки статус хода не показываем
  const turnStatus = (gameResults || isFieldCutscenePlaying) ? PLAYER_TURN_STATUSES.NONE : getTurnStatus(
    id,
    currentPlayer,
    phase,
    playersConfirmedSetupPhase,
    phaseCompletedPlayers,
    isParallelSupplyPhase
  );

  const displayedPlayerPolicy = player.policy;
  const displayedPlayerNextPolicy = getPlayerNextPolicy(player);
  const hasInitiative = sharedGameState.turnOrder.length > 0 && sharedGameState.turnOrder[0] === id;
  // Перехватил инициативу в этом раунде — первым ходит в войне. Кнопки
  // перехвата в списке нет (она в футере), поэтому кто перехватил — видно здесь
  const hasInterceptedInitiative = Boolean(sharedGameState.nextInitiativePlayerId)
    && sharedGameState.nextInitiativePlayerId === id;

  // Дизайн-система матча: карточки — базальт с закладкой цвета игрока, кто
  // ходит — показывает бегущая по периметру полоса (ее вид зависит от
  // рейтингового образа). Исключение — подготовка: там все карточки залиты
  // эмалью своего цвета, чтобы на старте было видно, кто какого цвета;
  // закончивших и там отличает только погасшая полоса
  const isPreparation = checkIfAnyGamePreparationPhase(phase);
  const rankStripe = getRankStripe(selectedSkin);

  return (
    <div
      className={joinClassNames(
        'gui-plate',
        isPreparation ? 'gui-plate_mine' : 'gui-plate_bar',
        'gui-shape_s',
        'player-info',
        isPreparation && 'player-info_colored-mode',
        isOwn && 'player-info_own',
        turnStatus !== PLAYER_TURN_STATUSES.NONE && `player-info_turn-${turnStatus}`,
        className,
      )}
      style={getPlayerColorStyle(playerColor)}
      data-turn-status={turnStatus}
      data-rank-stripe={rankStripe}
      onClick={() => {
        if(onClick) {
          playCardSound();
          onClick();
        }
      }}
    >
      {/* Ходит — бегущая по периметру полоса: вращающиеся слои под лицом
          плиты, у алмазного образа еще два слоя искр; у алмазного и
          рубинового лицо пульсирует светом (PlayerInfo.css) */}
      {turnStatus === PLAYER_TURN_STATUSES.ACTING && (
        <span className={'player-info_run'} aria-hidden={true}>
          <span className={'player-info_run-spin'}/>
          {rankStripe === 'diamond' && (
            <>
              <span className={'player-info_run-glints player-info_run-glints_a'}/>
              <span className={'player-info_run-glints player-info_run-glints_b'}/>
            </>
          )}
        </span>
      )}
      {turnStatus === PLAYER_TURN_STATUSES.ACTING && (rankStripe === 'diamond' || rankStripe === 'ruby') && (
        <span className={'player-info_glow'} aria-hidden={true}/>
      )}

      {/* Цвет игрока — эмалевая закладка по левому краю карточки */}
      <span className={'player-info_color-tab'} aria-hidden={true}/>

      <div className={'player-info-container'}>
        <div className={'player-info_faction-icon-container'}>
          <img
            className={'player-info_faction-icon'}
            src={resolveFactionSkinIconPath(factionId, selectedSkin)}
            alt={factionId}
          />
          <RankSkinFrame skinId={selectedSkin}/>
          {checkIfBotMarkShown(player) && <VisibleBotMark player={player}/>}
          {/* Своя карточка — эмалевая лента «вы» цвета игрока по нижнему
              краю портрета: не отнимает места у имени */}
          {isOwn && (
            <span className={'player-info_own-badge'} title={'Это вы'}>вы</span>
          )}
          {/* Инициатива и перехват — на углах портрета, как у кружка в строке
              игроков шапки: место в строке имени остается имени */}
          {hasInitiative && (
            <img
              src="/img/icons/initiative.png"
              className={'player-info_initiative-icon'}
              alt="Инициатива"
              title={'Инициатива (право первого хода в фазе)'}
            />
          )}
          {hasInterceptedInitiative && (
            <img
              src="/img/icons/action initiative active.png"
              className={'player-info_initiative-icon player-info_interception-icon'}
              alt="Перехват инициативы"
              title={'Перехватил инициативу — первым ходит в войне'}
            />
          )}
        </div>

        {/* Верхняя строка: имя, справа — ресурсы, прижатые к верху карточки.
            Нижняя строка целиком отдана политике — с переходом на следующую
            ей нужна вся ширина */}
        <div className={'player-info_name-row'}>
          <div className={'player-info-name'}>
            {name}
          </div>
          {controlLabel && (
            <div className={'player-info-control-label'}>
              {controlLabel}
            </div>
          )}
        </div>

        {/* Ресурсы — тем же шрифтом, что полоса ресурсов в шапке */}
        <div className={'player-info_resources-container'}>
          <StatWell
            bare={true}
            icon={'/img/icons/coins.png'}
            value={coins}
            income={coinsIncome}
            title={'Монеты'}
          />
          <StatWell
            bare={true}
            icon={'/img/icons/win points.png'}
            value={winPoints}
            income={winPointsIncome}
            title={'Очки победы'}
          />
        </div>

        {isGameStarted && phase !== PHASES.SETUP && (
          <div className={'player-info_policy-container'}>
            {displayedPlayerPolicy ? (
              <>
                <div
                  className={'player-info_policy'}
                  onClick={(e) => {
                    e.stopPropagation();

                    playCardSound();
                    onPolicyClick(displayedPlayerPolicy);
                  }}
                >
                  <LoadedImage
                    className={'player-info_policy-img'}
                    src={getPolicyImagePath(displayedPlayerPolicy.id)}
                    alt={displayedPlayerPolicy.name}
                  />
                  <div className={'player-info_policy-name'}>
                    {displayedPlayerPolicy.name}
                  </div>
                </div>

                {displayedPlayerNextPolicy ? (
                  <>
                    <div className="player-info-policy-from-to">
                      <img
                        src={'/img/icons/up white.png'}
                        alt={'right arrow'}
                      />
                    </div>

                    <div
                      className={'player-info_policy'}
                      onClick={(e) => {
                        e.stopPropagation();

                        playCardSound();
                        onPolicyClick(displayedPlayerNextPolicy);
                      }}
                    >
                      <LoadedImage
                        className={'player-info_policy-img'}
                        src={getPolicyImagePath(displayedPlayerNextPolicy.id)}
                        alt={displayedPlayerNextPolicy.name}
                      />
                      <div className={'player-info_policy-name'}>
                        {displayedPlayerNextPolicy.name}
                      </div>
                    </div>
                  </>
                ) : null}
              </>
            ) : <div className={'player-info_no-policy'}>
              Нет политики
            </div>}
          </div>
        )}
      </div>
    </div>
  );
}
